import React, { useState, useEffect } from "react";

/* ============================================================================
   ELVIS ALOHA ARCHIVE — PREMIUM COLLECTION SHOWCASE
   
   A museum-quality presentation of the complete historical archive.
   Designed for collectors, institutions, museums, and researchers.
   
   Focus: Historical significance, provenance, documentation, rarity.
   No sales language. Scholarly, authoritative, restrained.
   ============================================================================ */

// Collection imagery
const IMAGES = {
  heroCollection: "/assets/collection/real-graceland-costume-tickets-2.jpg",
  globePortrait: "/assets/collection/real-proof-globe-portrait-1.jpg",
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  gracelandDisplay: "/assets/collection/real-graceland-framed-tickets-1.jpg"
};

/* ============================================================================
   SMOOTH SCROLL UTILITY
   ============================================================================ */
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 72;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

/* ============================================================================
   NAVIGATION
   ============================================================================ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'history', label: 'History' },
    { id: 'tickets', label: 'The Tickets' },
    { id: 'artifacts', label: 'Artifacts' },
    { id: 'provenance', label: 'Provenance' },
    { id: 'research', label: 'Research' },
    { id: 'about', label: 'About' }
  ];

  return (
    <>
      <style>{`
        .showcase-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: all 0.3s ease;
        }
        .showcase-nav.scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 1px 0 rgba(0,0,0,0.06);
        }
        .showcase-nav:not(.scrolled) {
          background: transparent;
        }
        .showcase-nav-link {
          padding: 8px 14px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: ${scrolled ? '#333' : 'rgba(255,255,255,0.85)'};
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
        }
        .showcase-nav-link:hover {
          color: ${scrolled ? '#8B7355' : '#C9A962'};
        }
        @media (max-width: 900px) {
          .showcase-desktop-nav { display: none !important; }
          .showcase-mobile-toggle { display: flex !important; }
        }
        @media (min-width: 901px) {
          .showcase-desktop-nav { display: flex !important; }
          .showcase-mobile-toggle { display: none !important; }
        }
      `}</style>

      <nav className={`showcase-nav ${scrolled ? 'scrolled' : ''}`} data-testid="showcase-nav">
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '18px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
             style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '16px',
              fontWeight: 600,
              color: scrolled ? '#1a1a1a' : 'white',
              letterSpacing: '0.08em'
            }}>
              ELVIS ALOHA ARCHIVE
            </span>
          </a>

          <div className="showcase-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {navItems.map(item => (
              <button key={item.id} className="showcase-nav-link" onClick={() => scrollToSection(item.id)}>
                {item.label}
              </button>
            ))}
          </div>

          <button className="showcase-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', 
                     background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={scrolled ? '#1a1a1a' : 'white'} strokeWidth="1.5">
              {mobileOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                         : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div style={{ background: 'white', borderTop: '1px solid #eee', padding: '16px 24px' }}>
            {navItems.map(item => (
              <button key={item.id} onClick={() => { scrollToSection(item.id); setMobileOpen(false); }}
                style={{ display: 'block', width: '100%', padding: '14px 0', textAlign: 'left', background: 'none', border: 'none',
                  fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: '#333', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </>
  );
};

/* ============================================================================
   SECTION: HERO INTRODUCTION
   ============================================================================ */
const HeroSection = () => (
  <section id="top" style={{
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: '#0f0f0f',
    overflow: 'hidden'
  }} data-testid="hero-section">
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${IMAGES.heroCollection})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center 30%',
      opacity: 0.2
    }} />
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(15,15,15,0.7) 0%, rgba(15,15,15,0.5) 40%, rgba(15,15,15,0.9) 100%)'
    }} />

    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '140px 32px 100px',
      textAlign: 'center'
    }}>
      <p style={{
        marginBottom: '20px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: '#C9A962'
      }}>
        A Complete Historical Archive
      </p>

      <h1 style={{
        margin: '0 0 28px',
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(32px, 7vw, 64px)',
        fontWeight: 400,
        lineHeight: 1.1,
        color: 'white',
        letterSpacing: '-0.01em'
      }}>
        The Elvis Presley<br />
        <em>Aloha from Hawaii</em><br />
        Collection
      </h1>

      <p style={{
        maxWidth: '680px',
        margin: '0 auto 40px',
        fontFamily: "'Source Serif 4', serif",
        fontSize: 'clamp(15px, 2vw, 18px)',
        lineHeight: 1.75,
        color: 'rgba(255,255,255,0.75)'
      }}>
        Original tickets, production materials, printing proofs, photographs, and documents 
        from the world's first global satellite concert broadcast—preserved as a unified 
        historical archive documenting January 14, 1973.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '40px' }}>
        {[
          { value: '50+', label: 'Original Tickets' },
          { value: 'Complete', label: 'Production Archive' },
          { value: 'Documented', label: 'Provenance' }
        ].map((stat, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 500, color: '#C9A962' }}>
              {stat.value}
            </p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 600, 
                        letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>

    <button onClick={() => scrollToSection('overview')} style={{
      position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
      padding: '12px'
    }} aria-label="Scroll to content">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="m6 9 6 6 6-6"/>
      </svg>
    </button>
  </section>
);

/* ============================================================================
   SECTION: COLLECTION OVERVIEW
   ============================================================================ */
const OverviewSection = () => (
  <section id="overview" style={{ padding: '100px 32px', background: 'white' }} data-testid="overview-section">
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355', textAlign: 'center' }}>
        Collection Overview
      </p>
      <h2 style={{ margin: '0 0 32px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 38px)', 
                   fontWeight: 400, color: '#1a1a1a', lineHeight: 1.25, textAlign: 'center' }}>
        A Unified Archive, Not a Group of Unrelated Items
      </h2>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '16px', lineHeight: 1.85, color: '#444', marginBottom: '48px' }}>
        <p style={{ marginBottom: '20px' }}>
          The Elvis Aloha Archive preserves the complete documented record of the Aloha from Hawaii 
          concert ticket production—from original Globe Tickets printing proofs through finished tickets, 
          correspondence, photographs, and supporting materials. These items form an interconnected 
          historical archive that traces the full lifecycle of this landmark production.
        </p>
        <p>
          Unlike scattered memorabilia, this collection maintains the relationships between items: 
          production proofs connect to finished tickets, photographs document the printing process, 
          correspondence establishes dates and decisions, and contemporaneous newspaper coverage 
          provides independent verification. Together, they tell a complete story that individual 
          pieces cannot convey alone.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
        {[
          { title: 'Concert Tickets', count: '50+', desc: 'Original tickets spanning multiple price points and seating sections' },
          { title: 'Production Proofs', count: 'Archive', desc: 'Globe Tickets printing proofs, color tests, and progressive separations' },
          { title: 'Photographs', count: 'Collection', desc: 'Original photographs documenting the production and event' },
          { title: 'Documents', count: 'Complete', desc: 'Correspondence, contracts, and contemporaneous records' }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '24px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
            <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, 
                        letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8B7355' }}>
              {item.count}
            </p>
            <h3 style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>
              {item.title}
            </h3>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.6, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: HISTORICAL CONTEXT
   ============================================================================ */
const HistorySection = () => (
  <section id="history" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="history-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '64px', alignItems: 'start' }}>
        <div>
          <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                      letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
            Historical Context
          </p>
          <h2 style={{ margin: '0 0 28px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 36px)', 
                       fontWeight: 400, color: '#1a1a1a', lineHeight: 1.25 }}>
            The First Global Satellite Concert Broadcast
          </h2>

          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '15px', lineHeight: 1.85, color: '#444' }}>
            <p style={{ marginBottom: '18px' }}>
              On January 14, 1973, Elvis Presley performed at the Honolulu International Center Arena 
              in what became the first entertainment program transmitted globally via satellite. The 
              <em> Aloha from Hawaii Via Satellite</em> concert was broadcast live to participating 
              markets across Asia and Oceania, including Japan, South Korea, the Philippines, Australia, 
              and New Zealand.
            </p>
            <p style={{ marginBottom: '18px' }}>
              The concert was organized as a benefit for the Kui Lee Cancer Fund. On February 4, 1973, 
              Elvis presented a check for $77,000 to the charity—a moment documented in photographs 
              preserved in this archive.
            </p>
            <p style={{ marginBottom: '18px' }}>
              The United States broadcast followed on April 4, 1973, on NBC. Scheduling in other 
              territories varied based on local broadcasting arrangements. The program established 
              new records for television viewership and remains one of the most significant broadcasts 
              in entertainment history.
            </p>
            <p>
              Globe Tickets of Los Angeles produced the concert tickets, creating distinctive designs 
              featuring Elvis's portrait. The surviving production materials in this archive document 
              the complete ticket creation process from initial proofs through finished tickets.
            </p>
          </div>
        </div>

        <div>
          <div style={{ marginBottom: '32px' }}>
            <img src={IMAGES.gracelandDisplay} alt="The Aloha from Hawaii ticket collection"
              style={{ width: '100%', height: 'auto', display: 'block' }} />
            <p style={{ marginTop: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', 
                        color: '#888', lineHeight: 1.5 }}>
              The collection documented at Graceland, September 14, 2022. This photograph establishes 
              the archive's presence at the most significant Elvis Presley historical location.
            </p>
          </div>

          <div style={{ padding: '24px', background: 'white', border: '1px solid #e0ddd8' }}>
            <h4 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, 
                         letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
              Key Dates
            </h4>
            <div style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#444' }}>
              {[
                { date: '1972', event: 'Planning and ticket production begins' },
                { date: 'January 12, 1973', event: 'Dress rehearsal concert' },
                { date: 'January 14, 1973', event: 'Live satellite broadcast to Asia/Oceania' },
                { date: 'February 4, 1973', event: '$77,000 presented to Kui Lee Cancer Fund' },
                { date: 'April 4, 1973', event: 'United States broadcast on NBC' }
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '16px', paddingBottom: '12px', marginBottom: '12px', 
                                        borderBottom: idx < 4 ? '1px solid #f0ede8' : 'none' }}>
                  <span style={{ fontWeight: 700, color: '#1a1a1a', minWidth: '120px', flexShrink: 0 }}>{item.date}</span>
                  <span style={{ color: '#555' }}>{item.event}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: THE 50 SPECIAL TICKETS
   ============================================================================ */
const TicketsSection = () => (
  <section id="tickets" style={{ padding: '100px 32px', background: '#0f0f0f', color: 'white' }} data-testid="tickets-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                    letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A962' }}>
          Collection Centerpiece
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 5vw, 42px)', 
                     fontWeight: 400, lineHeight: 1.2 }}>
          The Original Concert Tickets
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Serif 4', serif", fontSize: '16px', 
                    lineHeight: 1.8, color: 'rgba(255,255,255,0.75)' }}>
          More than fifty original tickets from the January 14, 1973 concert form the core of this archive. 
          This represents one of the most comprehensive known collections of Aloha from Hawaii tickets, 
          spanning multiple price points, seating sections, and condition grades.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '48px', alignItems: 'start' }}>
        <div>
          <img src={IMAGES.heroCollection} alt="Complete ticket archive display"
            style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '16px' }} />
          <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
            The complete ticket archive documented at Graceland, showing the scope and variety of preserved materials.
          </p>
        </div>

        <div>
          <h3 style={{ margin: '0 0 20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, 
                       letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>
            Ticket Characteristics
          </h3>
          <div style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.8, color: 'rgba(255,255,255,0.8)' }}>
            <p style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'white' }}>Price Points:</strong> The collection includes tickets at $10.00, $7.50, 
              and $5.00 face values, representing the three pricing tiers offered to the original concert audience.
            </p>
            <p style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'white' }}>Seating Sections:</strong> Floor, Arena, and Balcony sections are 
              represented, documenting the full range of venue seating available for the January 14, 1973 performance.
            </p>
            <p style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'white' }}>Condition Range:</strong> The archive includes both complete unused 
              tickets and used stubs with original tear lines, representing tickets as issued and as used at the event.
            </p>
            <p style={{ marginBottom: '16px' }}>
              <strong style={{ color: 'white' }}>Design Elements:</strong> Globe Tickets of Los Angeles produced 
              these tickets featuring the distinctive portrait design. Color variations and printing characteristics 
              are documented across the collection.
            </p>
            <p>
              <strong style={{ color: 'white' }}>Provenance:</strong> Acquisition records trace these tickets through 
              documented sources. The collection was photographed at Graceland in September 2022, establishing its 
              historical significance.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: FEATURED ARTIFACTS
   ============================================================================ */
const ArtifactsSection = () => (
  <section id="artifacts" style={{ padding: '100px 32px', background: 'white' }} data-testid="artifacts-section">
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                    letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
          Featured Materials
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 38px)', 
                     fontWeight: 400, color: '#1a1a1a' }}>
          Production Archive & Documents
        </h2>
        <p style={{ maxWidth: '650px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', 
                    lineHeight: 1.7, color: '#666' }}>
          Beyond the tickets themselves, the archive preserves production materials that document 
          how these historic items were created.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px' }}>
        {[
          {
            image: IMAGES.globePortrait,
            category: 'Production Proof',
            title: 'Globe Tickets Portrait Design',
            date: '1972-1973',
            description: 'Original production proof from Globe Tickets featuring the iconic portrait design used on the concert tickets. This proof documents the printing process and represents a key production artifact.',
            significance: 'Production proofs are rarer than the tickets themselves, as most were discarded after the printing run was complete.'
          },
          {
            image: IMAGES.artworkProofs,
            category: 'Color Separation Archive',
            title: 'Progressive Printing Proofs',
            date: '1972-1973',
            description: 'Complete set of progressive color proofs showing each stage of the ticket printing process. These materials document how the final design was achieved through precise color registration.',
            significance: 'These proofs provide documentary evidence of the printing methodology used by Globe Tickets for this production.'
          },
          {
            image: IMAGES.documents,
            category: 'Production Documents',
            title: 'Correspondence & Records',
            date: '1972-1973',
            description: 'Original correspondence, contracts, and planning documents from the concert production. These materials provide contemporaneous documentation of decisions and timelines.',
            significance: 'Documents establish provenance and provide independent verification of production details.'
          },
          {
            image: IMAGES.ticketDetail,
            category: 'Detailed Documentation',
            title: 'Ticket Design Variations',
            date: 'January 1973',
            description: 'Close examination photographs documenting printing characteristics, color variations, and design details across the ticket collection.',
            significance: 'Detailed documentation enables authentication and establishes the relationships between tickets in the archive.'
          }
        ].map((item, idx) => (
          <article key={idx} style={{ background: '#fafaf8', border: '1px solid #e8e6e2' }}>
            <div style={{ position: 'relative', paddingTop: '66%', overflow: 'hidden', background: '#e8e6e2' }}>
              <img src={item.image} alt={item.title}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, 
                            letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
                  {item.category}
                </p>
                <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', color: '#999' }}>
                  {item.date}
                </p>
              </div>
              <h3 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 500, color: '#1a1a1a' }}>
                {item.title}
              </h3>
              <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.65, color: '#555' }}>
                {item.description}
              </p>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', lineHeight: 1.6, 
                          color: '#888', fontStyle: 'italic', paddingTop: '12px', borderTop: '1px solid #e8e6e2' }}>
                {item.significance}
              </p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: PROVENANCE & PRESERVATION
   ============================================================================ */
const ProvenanceSection = () => (
  <section id="provenance" style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="provenance-section">
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                    letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
          Documentation
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 38px)', 
                     fontWeight: 400, color: '#1a1a1a' }}>
          Provenance & Preservation
        </h2>
      </div>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '16px', lineHeight: 1.85, color: '#444', marginBottom: '48px' }}>
        <p style={{ marginBottom: '20px' }}>
          The Elvis Aloha Archive has been assembled through documented acquisitions from established sources, 
          including direct acquisition from Globe Tickets production archives and period collectors with known 
          ownership histories. Each significant item in the collection has provenance documentation tracing 
          its chain of custody.
        </p>
        <p>
          The archive has been professionally photographed, catalogued, and researched using contemporaneous 
          1972-1973 sources. It was documented at Graceland on September 14, 2022, establishing its presence 
          at the most significant Elvis Presley historical location.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        {[
          { title: 'Acquisition Records', desc: 'Complete documentation of how materials entered the archive, including source identification and acquisition dates.' },
          { title: 'Graceland Documentation', desc: 'Photographic evidence establishing the collection\'s presence at Graceland in September 2022.' },
          { title: 'Professional Photography', desc: 'High-resolution documentation of each significant item for authentication and scholarly reference.' },
          { title: 'Source Research', desc: 'Verification using contemporaneous newspaper coverage, broadcast records, and primary documentation.' },
          { title: 'Condition Reports', desc: 'Detailed assessment of physical condition for tickets, proofs, and documents in the archive.' },
          { title: 'Evidence Classification', desc: 'Each claim is classified by source evidence tier, distinguishing established fact from qualified conclusion.' }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '20px', background: 'white', border: '1px solid #e0ddd8' }}>
            <h4 style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
              {item.title}
            </h4>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.6, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: RESEARCH & RARITY
   ============================================================================ */
const ResearchSection = () => (
  <section id="research" style={{ padding: '100px 32px', background: 'white' }} data-testid="research-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '64px' }}>
        <div>
          <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                      letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
            Scholarly Foundation
          </p>
          <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 4vw, 32px)', 
                       fontWeight: 400, color: '#1a1a1a', lineHeight: 1.25 }}>
            Research & Documentation
          </h2>
          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '15px', lineHeight: 1.85, color: '#444' }}>
            <p style={{ marginBottom: '16px' }}>
              The archive is supported by extensive research using contemporaneous sources. This includes 
              newspaper coverage from 1972-1973, broadcast records, production correspondence, and 
              physical examination of artifacts.
            </p>
            <p style={{ marginBottom: '16px' }}>
              An evidence matrix classifies each claim by source tier: contemporaneous primary sources, 
              contemporaneous secondary sources, later retrospective accounts, and physical artifact 
              examination. This methodology ensures transparent distinction between established fact 
              and qualified conclusion.
            </p>
            <p>
              Open questions are acknowledged rather than filled with speculation. Where evidence is 
              incomplete or contradictory, the archive documents what is known and what remains unresolved.
            </p>
          </div>
        </div>

        <div>
          <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                      letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
            Significance
          </p>
          <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 4vw, 32px)', 
                       fontWeight: 400, color: '#1a1a1a', lineHeight: 1.25 }}>
            Rarity & Importance
          </h2>
          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '15px', lineHeight: 1.85, color: '#444' }}>
            <p style={{ marginBottom: '16px' }}>
              <strong>Production Materials:</strong> The printing proofs, color separations, and production 
              documents in this archive are significantly rarer than the tickets themselves. Most production 
              materials were discarded after printing runs were complete.
            </p>
            <p style={{ marginBottom: '16px' }}>
              <strong>Archive Completeness:</strong> The collection preserves relationships between items 
              that would be lost if materials were scattered. Production proofs connect to finished tickets; 
              correspondence establishes timelines; photographs document the complete process.
            </p>
            <p>
              <strong>Historical Documentation:</strong> This archive documents a genuine milestone in 
              broadcast history. The January 14, 1973 concert was the first entertainment program 
              transmitted globally via satellite, establishing records that stood for decades.
            </p>
          </div>
        </div>
      </div>

      {/* Unresolved Questions */}
      <div style={{ marginTop: '64px', padding: '32px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, 
                     letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
          Unresolved Research Questions
        </h3>
        <p style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#555' }}>
          The archive acknowledges areas where evidence remains incomplete:
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.8, color: '#666' }}>
          <li style={{ marginBottom: '8px' }}>Exact total print run quantities for each ticket price point</li>
          <li style={{ marginBottom: '8px' }}>Complete chain of custody for certain production materials prior to documented acquisition</li>
          <li style={{ marginBottom: '8px' }}>Identification of all Globe Tickets personnel involved in the production</li>
          <li>Precise dates for certain production decisions documented in correspondence</li>
        </ul>
        <p style={{ margin: '16px 0 0', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.6, color: '#888', fontStyle: 'italic' }}>
          These questions are documented to distinguish established fact from areas requiring further research.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: COLLECTION GALLERY
   ============================================================================ */
const GallerySection = () => (
  <section style={{ padding: '100px 32px', background: '#f8f7f5' }} data-testid="gallery-section">
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '56px' }}>
        <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                    letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
          Visual Archive
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 38px)', 
                     fontWeight: 400, color: '#1a1a1a' }}>
          Collection Gallery
        </h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
        {[
          { image: IMAGES.heroCollection, title: 'Complete Ticket Archive', category: 'Tickets', caption: 'Over 50 original concert tickets spanning multiple price points and seating sections' },
          { image: IMAGES.globePortrait, title: 'Portrait Design Proof', category: 'Production', caption: 'Original Globe Tickets production proof featuring the iconic portrait design' },
          { image: IMAGES.artworkProofs, title: 'Progressive Color Proofs', category: 'Production', caption: 'Color separation proofs documenting the printing process' },
          { image: IMAGES.documents, title: 'Production Documents', category: 'Documents', caption: 'Original correspondence and planning materials from 1972-1973' },
          { image: IMAGES.ticketDetail, title: 'Ticket Detail Study', category: 'Documentation', caption: 'Close examination of printing characteristics and design variations' },
          { image: IMAGES.gracelandDisplay, title: 'Graceland Documentation', category: 'Provenance', caption: 'The collection documented at Graceland, September 14, 2022' }
        ].map((item, idx) => (
          <figure key={idx} style={{ margin: 0, background: 'white', border: '1px solid #e0ddd8' }}>
            <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden' }}>
              <img src={item.image} alt={item.title}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <figcaption style={{ padding: '16px' }}>
              <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, 
                          letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B7355' }}>
                {item.category}
              </p>
              <h4 style={{ margin: '0 0 6px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
                {item.title}
              </h4>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', lineHeight: 1.5, color: '#888' }}>
                {item.caption}
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   SECTION: ABOUT THE COLLECTION
   ============================================================================ */
const AboutSection = () => (
  <section id="about" style={{ padding: '100px 32px', background: 'white' }} data-testid="about-section">
    <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
      <p style={{ marginBottom: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, 
                  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8B7355' }}>
        Purpose
      </p>
      <h2 style={{ margin: '0 0 32px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px, 4vw, 38px)', 
                   fontWeight: 400, color: '#1a1a1a' }}>
        About the Archive
      </h2>

      <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '16px', lineHeight: 1.85, color: '#444', textAlign: 'left' }}>
        <p style={{ marginBottom: '20px' }}>
          The Elvis Aloha Archive exists to preserve and document original materials from one of the most 
          significant broadcasts in entertainment history. The archive maintains these materials together 
          as a unified collection, preserving the relationships between items that would be lost if the 
          materials were scattered.
        </p>
        <p style={{ marginBottom: '20px' }}>
          The archive is committed to accurate historical presentation. Claims are supported by evidence, 
          uncertain findings are appropriately qualified, and open questions are acknowledged rather than 
          filled with speculation. This methodology ensures the historical record remains reliable for 
          future researchers.
        </p>
        <p>
          The January 14, 1973 concert represents a genuine milestone in broadcast history. The materials 
          in this archive document how that event was organized, promoted, and experienced by the original 
          audience. Preserving these materials together ensures that future generations can understand 
          this moment in its full historical context.
        </p>
      </div>

      <div style={{ marginTop: '48px', padding: '32px', background: '#fafaf8', border: '1px solid #e8e6e2' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, 
                     color: '#1a1a1a' }}>
          For Inquiries
        </h3>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#666' }}>
          Researchers, institutions, and serious inquiries may contact the archive for additional information, 
          access to documentation, or scholarly consultation.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   FOOTER
   ============================================================================ */
const Footer = () => (
  <footer style={{ padding: '56px 32px 32px', background: '#1a1a1a', color: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '40px', marginBottom: '40px' }}>
        <div style={{ maxWidth: '320px' }}>
          <p style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 600, letterSpacing: '0.08em' }}>
            ELVIS ALOHA ARCHIVE
          </p>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)' }}>
            Preserving the documented history of Elvis Presley's historic 1973 Aloha from Hawaii satellite broadcast.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, 
                        letterSpacing: '0.15em', textTransform: 'uppercase', color: '#C9A962' }}>
              Sections
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['Overview', 'History', 'Tickets', 'Artifacts', 'Provenance', 'Research'].map(item => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase())}
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', 
                           fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  {item}
                </button>
              ))}
            </nav>
          </div>
          <div>
            <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, 
                        letterSpacing: '0.15em', textTransform: 'uppercase', color: '#C9A962' }}>
              Legal
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="/rights" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Rights</a>
              <a href="/privacy" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy</a>
              <a href="/accessibility" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Accessibility</a>
            </nav>
          </div>
        </div>
      </div>

      <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', lineHeight: 1.7, color: 'rgba(255,255,255,0.4)' }}>
          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Independent Archive:</strong> Elvis Aloha Archive 
          is an independent historical archive. Not affiliated with Elvis Presley Enterprises, Graceland, 
          or any official Elvis Presley organization. Names and trademarks remain the property of their respective rights holders.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
          © {new Date().getFullYear()} Elvis Aloha Archive
        </p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   MAIN SHOWCASE PAGE
   ============================================================================ */
const PremiumShowcasePage = () => {
  return (
    <div data-testid="premium-showcase-page" style={{ background: 'white' }}>
      <Navigation />
      <HeroSection />
      <OverviewSection />
      <HistorySection />
      <TicketsSection />
      <ArtifactsSection />
      <ProvenanceSection />
      <ResearchSection />
      <GallerySection />
      <AboutSection />
      <Footer />
    </div>
  );
};

export default PremiumShowcasePage;
